import type { TrackingProps } from '@seek/tealium-adapter/lib-types/types';
import { Box } from 'braid-design-system';
import React from 'react';

import { useTracking } from '../../../hooks/useTracking';

import { Overlay } from './braidPrivate/Overlay';
import { virtualTouchable } from './braidPrivate/touchable/virtualTouchable';

import * as styles from './braidPrivate/IconButton.css';

type BoxProps = React.ComponentProps<typeof Box>;

interface IconButtonWithoutTooltipProps {
  href?: string;
  onClick?(): void;
  icon: React.ReactNode;
  disabled?: boolean;
  active?: boolean;
  hoverBackground?: BoxProps['background'];
  eventName?: string;
  eventAttributes?: TrackingProps;
  dataTestId?: string;
}

export const IconButtonWithoutTooltip = React.forwardRef<
  HTMLButtonElement,
  React.PropsWithChildren<IconButtonWithoutTooltipProps>
>(
  (
    {
      href,
      icon,
      disabled = false,
      active = false,
      hoverBackground = 'surface',
      onClick,
      eventName,
      eventAttributes,
      dataTestId,
    },
    forwardedRef,
  ) => {
    const { trackEvent } = useTracking();

    const onClickWithTrackingEvent = () => {
      if (eventName) {
        trackEvent(eventName, eventAttributes);
      }

      onClick?.();
    };

    return (
      <Box
        component={href ? 'a' : 'button'}
        type={href ? 'a' : 'button'}
        ref={forwardedRef}
        cursor="pointer"
        outline="none"
        className={[
          disabled ? styles.disabledButton : styles.button,
          virtualTouchable(),
        ]}
        zIndex={0}
        transform={{ active: 'touchable' }}
        transition="touchable"
        href={disabled ? undefined : href}
        onClick={disabled ? undefined : onClickWithTrackingEvent}
      >
        <Box
          position="relative"
          display="flex"
          className={styles.iconSize}
          alignItems="center"
          justifyContent="center"
          pointerEvents="none"
        >
          {!disabled && (
            <Overlay
              background={hoverBackground}
              transition="fast"
              borderRadius="full"
              className={[styles.hoverOverlay, active && styles.forceActive]}
            />
          )}
          {!disabled && (
            <Overlay
              boxShadow="outlineFocus"
              transition="fast"
              borderRadius="full"
              className={styles.focusOverlay}
              onlyVisibleForKeyboardNavigation
            />
          )}
          <Box
            position="relative"
            className={[styles.iconSize, disabled && styles.disabledIcon]}
            data-testid={dataTestId}
          >
            {icon}
          </Box>
        </Box>
      </Box>
    );
  },
);
